import clsx from 'clsx';
import { Flame, Loader2 } from 'lucide-react';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  count?: number;
  isActive?: boolean;
  onClick?: () => void;
  badge?: string;
  color?: 'amber' | 'blue' | 'emerald' | 'rose' | 'violet';
  /** When true, reserves space for the count chip even when count is 0 to prevent layout shifts */
  reserveCountSpace?: boolean;
  /** Shows overdue count with warning icon */
  overdueCount?: number;
  /** Shows loading spinner instead of count */
  isLoading?: boolean;
}

export function SidebarItem({
  icon,
  label,
  count,
  isActive,
  onClick,
  badge,
  color,
  reserveCountSpace,
  overdueCount,
  isLoading,
}: SidebarItemProps) {
  const colorClasses = {
    amber: 'text-accent-amber',
    blue: 'text-accent-blue',
    emerald: 'text-accent-emerald',
    rose: 'text-accent-rose',
    violet: 'text-accent-violet',
  };

  const showCount = count !== undefined && count > 0;
  const showOverdue = overdueCount !== undefined && overdueCount > 0;
  // Reserve space for count chip when it has content, when loading, or when explicitly requested.
  // Don't reserve space when the overdue badge is already visible and count is empty.
  const shouldReserveSpace = (reserveCountSpace || isLoading || showCount) && !(!showCount && !isLoading && showOverdue);

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[11px] transition-colors',
        isActive
          ? 'bg-accent-violet-dim text-accent-violet'
          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
      )}
    >
      <span
        className={clsx(
          isActive ? 'text-accent-violet' : color ? colorClasses[color] : 'text-text-quaternary'
        )}
      >
        {icon}
      </span>
      <span className="flex-1 text-left font-medium">{label}</span>
      {badge && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-accent-violet/10 text-accent-violet">
          {badge}
        </span>
      )}
      {/* Overdue count with warning icon - always rendered when prop is passed to prevent layout shift */}
      {overdueCount !== undefined && (
        <span
          className={clsx(
            'flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-accent-rose/10 text-accent-rose transition-opacity duration-200',
            showOverdue ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <Flame className="w-3 h-3" />
          {showOverdue ? overdueCount.toLocaleString() : '0'}
        </span>
      )}
      {/* Count/Loader container - always reserves space to prevent layout shifts */}
      {shouldReserveSpace && (
        <span
          className={clsx(
            'min-w-[24px] h-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums text-center flex items-center justify-center transition-all duration-200',
            (isLoading || showCount) ? 'opacity-100' : 'opacity-0',
            color === 'rose'
              ? 'bg-accent-rose/10 text-accent-rose'
              : 'bg-surface-tertiary text-text-quaternary'
          )}
        >
          {isLoading && <Loader2 className="w-3 h-3 text-text-quaternary animate-spin mr-0.5" />}
          {showCount ? count.toLocaleString() : '0'}
        </span>
      )}
    </button>
  );
}
